/**
 * Canonicalization and SHA-256 helpers.
 *
 * This module deliberately has no imports so `scripts/verify-report.ts` can load it directly with
 * Node's built-in TypeScript support and reproduce the hash shown in the UI from an exported report.
 */

/**
 * Deterministic JSON: object keys sorted recursively, array order preserved, no whitespace.
 * Non-finite numbers, undefined, functions and other non-JSON values become strings or null so the
 * output is identical in every engine.
 */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

export function canonicalize(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  switch (typeof value) {
    case 'string':
    case 'boolean':
      return value;
    case 'number':
      if (Number.isFinite(value)) return Object.is(value, -0) ? 0 : value;
      return String(value);
    case 'bigint':
      return value.toString();
    case 'object': {
      if (Array.isArray(value)) return value.map(canonicalize);
      const out: Record<string, unknown> = {};
      for (const key of Object.keys(value as object).sort()) {
        const v = (value as Record<string, unknown>)[key];
        if (v === undefined || typeof v === 'function') continue;
        out[key] = canonicalize(v);
      }
      return out;
    }
    default:
      return String(value);
  }
}

export async function sha256Hex(input: string | BufferSource): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('Web Crypto unavailable (page must be served over HTTPS or localhost)');
  const data: BufferSource = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  const digest = await subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** SHA-256 of the canonical JSON form of `value`. */
export function hashCanonical(value: unknown): Promise<string> {
  return sha256Hex(canonicalJson(value));
}

/** Placeholder written into the stable-hash input instead of a value that was shown to be randomized. */
export const RANDOMIZED_PLACEHOLDER = '[randomized]';
/** Placeholder for a signal whose API was missing or blocked (being blocked is itself stable). */
export const UNAVAILABLE_PLACEHOLDER = '[unavailable]';

/** Number of hex characters of the stable hash shown in the UI. */
export const SHORT_HASH_LENGTH = 16;
